/* eslint react/jsx-key: 0 */
import React, { ReactElement, PropsWithChildren, ReactNode } from 'react';
import {
  useTable,
  usePagination,
  TableOptions,
  useSortBy,
  useFlexLayout,
  useResizeColumns,
  PluginHook,
  useRowSelect,
} from 'react-table';
import classnames from 'classnames';
import { Icon } from '../../../Icon';
import { ResizeHandle } from '../ResizeHandle';
import { Pagination } from '../../../Pagination';
import styles from './index.module.css';

export interface TableProps<T extends Record<string, unknown>>
  extends TableOptions<T> {
  actions?: (data: T) => ReactNode;
  ariaLabel?: string;
  className?: string;
  pluginHooks?: PluginHook<T>[];
  rowClassName?: string;
}

const hooks = [
  useSortBy,
  useResizeColumns,
  useFlexLayout,
  usePagination,
  useRowSelect,
];

const DEFAULT_PAGE_SIZE = 100;

export function Table<T extends Record<string, unknown>>({
  actions,
  ariaLabel = '',
  className,
  columns,
  data,
  initialState,
  pluginHooks = [],
  rowClassName,
}: PropsWithChildren<TableProps<T>>): ReactElement {
  const tableHooks = [...hooks, ...pluginHooks];
  const tableInitialState: TableProps<T>['initialState'] = {
    pageSize: DEFAULT_PAGE_SIZE,
    ...initialState,
  };

  const {
    headerGroups,
    getTableBodyProps,
    getTableProps,
    gotoPage,
    prepareRow,
    page,
    pageCount,
    state: { pageIndex },
  } = useTable<T>(
    {
      columns,
      data,
      defaultColumn: {
        maxWidth: 300,
        minWidth: 20,
      },
      initialState: tableInitialState,
    },
    ...tableHooks,
  );

  const rowClasses = classnames(styles.row, rowClassName, {
    [styles.hasActions]: !!actions,
  });

  return (
    <>
      <div
        className={classnames('table', styles.table, className)}
        aria-label={ariaLabel}
        {...getTableProps()}
      >
        <div className={styles.head}>
          <div className={rowClasses}>
            {headerGroups.map((headerGroup) => (
              <div {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <div
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    className={styles.heading}
                  >
                    {column.render('Header')}
                    {column.isSorted ? (
                      <Icon
                        name="dropdown"
                        className={classnames(styles.icon, {
                          [styles.descending]: column.isSortedDesc,
                        })}
                      />
                    ) : (
                      ''
                    )}
                    {column.canResize && <ResizeHandle column={column} />}
                  </div>
                ))}
                {actions && <div className={styles.emptyHead}>&nbsp;</div>}
              </div>
            ))}
          </div>
        </div>
        <div {...getTableBodyProps()}>
          {page.map((row) => {
            prepareRow(row);

            return (
              <div {...row.getRowProps()} className={rowClasses}>
                {row.cells.map((cell) => {
                  return (
                    <div
                      {...cell.getCellProps()}
                      className={styles.col}
                      title={
                        typeof cell.value === 'string' ? cell.value : undefined
                      }
                    >
                      {cell.render('Cell')}
                    </div>
                  );
                })}
                {actions && (
                  <div className={styles.actions} data-test="actions">
                    {actions(row.original)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      {pageCount > 1 && (
        <div data-test="pagination">
          <Pagination
            current={pageIndex + 1}
            pages={pageCount}
            onClick={(page: number) => {
              gotoPage(page - 1);
            }}
          />
        </div>
      )}
    </>
  );
}
